/** @jsxImportSource @emotion/react */
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import slugify from 'react-slugify'

import { ERROR_403_MESSAGE } from '@app/app-constants'
import ArticleFooter from '@app/components/Guide/article/ArticleFooter'
import { ArticlePageHeader } from '@app/components/Guide/article/ArticlePage.Header'
import ArticleReferenceBlock from '@app/components/Guide/article/ArticleReferenceBlock'
import { ArticleResponse, fetchArticleBySlug } from '@app/components/Guide/hooks/useMainTab'
import { GuideMarkdownTailwind } from '@app/components/Guide/ui/GuideMarkdownTailwind'
import { GuideTableOfContents } from '@app/components/Guide/ui/GuideTableOfContents'
import { createTableOfContents } from '@app/components/Guide/utils/createTableOfContents'
import { toast } from '@app/components/ui/Toast/toast'
import { appLink } from '@app/config/appLink'

export const ArticlePage = () => {
  const navigate = useNavigate()
  const params = useParams()
  const {
    data: article,
    isLoading,
    error,
  } = useQuery<ArticleResponse>({
    queryKey: ['article-page', params.articleSlug],
    queryFn: () => fetchArticleBySlug(params.articleSlug as string),
    enabled: !!params.articleSlug,
  })

  useEffect(() => {
    if (error?.message === ERROR_403_MESSAGE) {
      toast({
        type: 'error',
        description:
          'You do not have permission to view this content. Please contact your administrator.',
        position: 'top-right',
      })
      navigate(appLink.homepage.path)
    }
  }, [error, navigate])

  if (isLoading) return <div>Loading...</div>
  if (error) return <div>Error</div>

  const { title, hero_image, authors, publishedAt, content_blocks } =
    (article && Array.isArray(article.data) && article.data[0]) || {}

  return (
    <div className="relative flex justify-center flex-1 w-full mx-auto max-w-7xl" id="article-page">
      <main className="flex-1 min-w-0 overflow-hidden">
        {/* <GuideBreadcrumbs links={breadcrumbs} /> */}
        <div className="w-full max-w-4xl py-8 mx-auto ">
          <ArticlePageHeader
            title={title as string}
            author={authors}
            publishedAt={publishedAt}
            read_time={content_blocks?.length}
            hero_image={hero_image?.formats?.small?.url}
          />

          <div className="space-y-6">
            {(article?.data?.[0]?.content_blocks ?? []).map((block, i) => {
              if (block.__component === 'content.content-section' && block.body) {
                return (
                  <section key={block.id ?? i} className="space-y-2">
                    {block.Title && (
                      <h3
                        id={slugify(block.Title)}
                        className="scroll-mt-[70px] text-xl font-bold leading-[150%] tracking-[0.32px] text-foreground"
                      >
                        {block.Title}
                      </h3>
                    )}

                    <GuideMarkdownTailwind markdown={block.body} />
                  </section>
                )
              }

              if (block.__component === 'content.references') {
                return (
                  <ArticleReferenceBlock
                    key={block.id}
                    id={block.id}
                    Label={block.Label}
                    Url={block.Url}
                  />
                )
              }

              return null
            })}
          </div>
          <ArticleFooter showScrollToTop={true} />
        </div>
      </main>
      <GuideTableOfContents
        tableOfContents={createTableOfContents(article?.data?.[0]?.content_blocks)}
      />
    </div>
  )
}

export default ArticlePage
